import { Home, Car, LayoutDashboard, User, LogOut, BookOpen } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const navRef = useRef<HTMLElement | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  // Keep CSS var in sync with the actual bottom nav height.
  // This ensures the Ricky FAB is always positioned ABOVE the bar on all devices.
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;

    const setVar = () => {
      const h = el.getBoundingClientRect().height;
      if (h && Number.isFinite(h)) {
        document.documentElement.style.setProperty("--mobile-bottom-nav-height", `${Math.round(h)}px`);
      }
    };

    setVar();
    const ro = new ResizeObserver(() => setVar());
    ro.observe(el);
    window.addEventListener("resize", setVar, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
    };
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchUserRole(session.user.id, session.user.email);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchUserRole(session.user.id, session.user.email);
      } else {
        setUserRole(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const fetchUserRole = async (userId: string, _email: string | undefined) => {
    // Use the has_role database function to check admin status first
    const { data: isAdmin } = await supabase
      .rpc('has_role', { _user_id: userId, _role: 'admin' });

    if (isAdmin) {
      setUserRole('admin');
      return;
    }

    // Check other roles from user_roles table
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();

    setUserRole(data?.role || 'leerling');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Uitgelogd",
      description: "Je bent succesvol uitgelogd.",
    });
    navigate("/");
  };

  const handleInstall = async () => {
    if (!deferredPrompt) {
      toast({
        title: "App installeren",
        description: "Open deze site in je browser en klik op 'Toevoegen aan startscherm' in het menu.",
      });
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === "accepted") {
      toast({
        title: "Geïnstalleerd!",
        description: "De app is toegevoegd aan je startscherm.",
      });
    }
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const getDashboardLink = () => {
    if (!session) return "/auth";
    
    switch (userRole) {
      case 'admin':
        return '/admin';
      case 'rijschool':
        return '/rijschool-dashboard';
      case 'partner':
        return '/partner-dashboard';
      case 'leerling':
      default:
        return '/leerling-dashboard';
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: "/", icon: Home, label: "Home" },
    { path: "/rijscholen", icon: Car, label: "Rijscholen" },
    { path: "/theorie-boeken", icon: BookOpen, label: "Boeken" },
    { path: getDashboardLink(), icon: LayoutDashboard, label: "Dashboard" },
  ];

  return (
    <nav
      ref={navRef}
      className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border shadow-lg lg:hidden"
      style={{ WebkitTransform: 'translateZ(0)' }}
    >
      <div className="flex items-center justify-between px-2 py-2">
        {/* Main Navigation */}
        <div className="flex items-center justify-around flex-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all ${
                isActive(item.path)
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/5"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          ))}
        </div>

        {/* Right side: Auth buttons */}
        <div className="flex items-center gap-1 pl-2 border-l border-border/50">

          {session ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="h-10 w-10 rounded-full text-muted-foreground"
              aria-label="Uitloggen"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="h-10 w-10 rounded-full text-muted-foreground"
              aria-label="Inloggen"
            >
              <Link to="/auth">
                <User className="w-5 h-5" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};
